import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  settingsService,
  type CardTerminal,
  type CreateCardTerminalInput,
  type UpdateCardTerminalInput,
} from '@/services/settings.service';
import { settingsKeys } from '@/services/queryKeys';
import { mockCardTerminals } from '@/mocks/tenantSettings';
import { isArrayResponse, withMockFallback } from '@/utils/queryMock';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function useCardTerminals() {
  return useQuery<CardTerminal[]>({
    queryKey: settingsKeys.cardTerminals(),
    queryFn: () =>
      withMockFallback(
        () => settingsService.listCardTerminals(),
        async () => {
          await sleep(120);
          return mockCardTerminals;
        },
        { label: 'settings.cardTerminals', validate: isArrayResponse },
      ),
    retry: 0,
  });
}

export function useCreateCardTerminal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateCardTerminalInput) =>
      withMockFallback(
        () => settingsService.createCardTerminal(body),
        async () =>
          ({
            _id: `mock-${Date.now()}`,
            tenant_id: 'mock-tenant',
            ...body,
            is_default: !!body.is_default,
          }) as CardTerminal,
        { label: 'settings.createCardTerminal' },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: settingsKeys.cardTerminals() }),
  });
}

export function useUpdateCardTerminal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: UpdateCardTerminalInput;
    }) =>
      withMockFallback(
        () => settingsService.updateCardTerminal(id, body),
        async () => ({ _id: id, ...body } as unknown as CardTerminal),
        { label: 'settings.updateCardTerminal' },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: settingsKeys.cardTerminals() }),
  });
}

export function useDeleteCardTerminal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      withMockFallback(
        () => settingsService.deleteCardTerminal(id),
        async () => ({ deleted: true }),
        { label: 'settings.deleteCardTerminal' },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: settingsKeys.cardTerminals() }),
  });
}

export function useSetDefaultCardTerminal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      withMockFallback(
        () => settingsService.setDefaultCardTerminal(id),
        async () => ({ _id: id, is_default: true } as unknown as CardTerminal),
        { label: 'settings.setDefaultCardTerminal' },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: settingsKeys.cardTerminals() }),
  });
}
